import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import type { EChartsType } from 'echarts/core';

export const joinStrFromList = (
  l: (string | undefined | null)[],
  symbol = ' / ',
) => l.filter((item) => item).join(symbol);

export const getOptionLabel = (
  options: { label: string; value: any }[],
  value: any,
) => {
  const filteredOption = options
    .filter((option) => option.value === value)
    .pop();
  return filteredOption?.label;
};

export const getMainMargin = (mainRef: React.RefObject<HTMLDivElement>) => {
  const firstChild = mainRef?.current?.firstChild as Element;

  return (
    (firstChild && window.getComputedStyle(firstChild))?.marginTop || '0px'
  );
};

/**
 * build tree from the array of objects
 * @param list The array.
 * @param idKeyName The key of id, usually 'id'.
 * @param recursiveKey The key of parent id, usually 'component_of'.
 * @param childrenKey The key of children arrays. It should not exist in the object in the array you provided, usually 'children'.
 */
export const buildTree = (
  list: any[],
  idKeyName: string,
  recursiveKey: string,
  childrenKey: string,
) => {
  const data = cloneDeep(list);
  const map: Record<string, any> = {};
  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    map[item[idKeyName]] = item;
  }
  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    const parentId = item[recursiveKey];
    if (parentId !== undefined && map[parentId]) {
      if (!map[parentId][childrenKey]) {
        map[parentId][childrenKey] = [];
      }
      map[parentId][childrenKey].push(item);
    }
  }

  return data.filter((item) => !item[recursiveKey]);
};

interface Tree<T extends Tree<T>> {
  children?: T[];
  value: string;
}
type Path = string[];
export const findPathByNode = <T extends Tree<T>>(
  forest: T[],
  toFind: string,
  path: string[] = [],
): Path | undefined => {
  for (let i = 0; i < forest.length; i++) {
    const node = forest[i];
    if (node.value === toFind) {
      return [...path, node.value];
    }
  }
  for (let i = 0; i < forest.length; i++) {
    const node = forest[i];
    if (node.children) {
      const result = findPathByNode(node.children, toFind, [
        ...path,
        node.value,
      ]);
      if (result) return result;
    }
  }
  return undefined;
};

export const isObjEmpty = (obj: Record<string, any>): boolean => {
  let isOk = true;
  const objKeys = Object.keys(obj);
  for (let i = 0; i < objKeys.length; i++) {
    const val = obj[objKeys[i]];
    if (Array.isArray(val) || !(val instanceof Object)) {
      isOk = isEmpty(val);
    } else {
      isOk = isObjEmpty(val);
    }
    if (!isOk) break;
  }
  return isOk;
};

export const isArrayEmpty = (
  array: Record<string, any>[] | null | undefined,
): boolean => {
  let isOk = true;
  if (!array || array.length === 0) {
    return isOk;
  }
  for (let i = 0; i < array.length; i++) {
    isOk = isObjEmpty(array[i]);
    if (!isOk) break;
  }
  return isOk;
};

export const getObjValue = (obj: Record<string, any>, key: string) => {
  let value: any = obj;
  key.split('.').forEach((subKey) => {
    value = value[subKey];
  });
  return value;
};

export const setObjValue = (
  obj: Record<string, any>,
  key: string,
  value: any,
) => {
  const subKeyList = key.split('.');
  subKeyList.forEach((subKey, index) => {
    if (index === subKeyList.length - 1) {
      obj[subKey] = value;
    } else {
      obj = obj[subKey];
    }
  });
};

//You can refer to https://github.com/vercel/next.js/discussions/36853
//useRouter unable to fetch query params with special characters
export const getQueryFromUrl = (url: string, query: string) => {
  const decodeUrl = decodeURI(url).split('?')[1];
  if (!decodeUrl) {
    return '';
  }
  return (
    decodeUrl
      .split('&')
      .reduce<Map<string, string>>((prev, curr) => {
        const [key, value] = curr.split('=');
        if (key && value) prev.set(key, value);
        return prev;
      }, new Map())
      .get(query) || ''
  );
};

export const scrollToBottom = () => {
  const scrollTop = document.scrollingElement?.scrollTop || 0;
  const windowHeight = document.scrollingElement?.clientHeight || 0;
  const scrollHeight = document.scrollingElement?.scrollHeight || 0;

  return scrollHeight <= scrollTop + windowHeight;
};

export const hasSameData = (
  obj: Record<string, any>,
  currentIndex: number,
  arr?: any[],
  omitOptions: string[] = [],
) => {
  let isSame = false;
  const omitObj = omit(obj, omitOptions);
  if (arr && Array.isArray(arr) && arr.length > 0) {
    for (let i = 0; i < arr.length; i++) {
      const omiValue = omit(arr[i], omitOptions);
      if (i !== currentIndex && isEqual(omitObj, omiValue)) {
        isSame = true;
        break;
      }
    }
  }
  return isSame;
};

export const judgeMouseDirection = (e: React.MouseEvent) => {
  const ele = e.target as HTMLElement;
  if (ele) {
    const width = ele.offsetWidth;
    const height = ele.offsetHeight;
    const left = ele.getBoundingClientRect().left;
    const top = ele.getBoundingClientRect().top;
    const x =
      (e.clientX - left - width / 2) * (width > height ? height / width : 1);
    const y =
      (e.clientY - top - height / 2) * (height > width ? width / height : 1);
    // 上(0) 右(1) 下(2) 左(3)
    return Math.round((Math.atan2(y, x) * (180 / Math.PI) + 180) / 90 + 3) % 4;
  }
  return undefined;
};

export const getVideoBase64 = (url: string, width = 340, height = 200) => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.setAttribute('crossOrigin', 'anonymous');
    video.setAttribute('src', url);
    video.setAttribute('width', String(width));
    video.setAttribute('height', String(height));
    video.setAttribute('preload', 'auto');
    video.setAttribute('muted', 'muted');
    video.addEventListener('loadeddata', () => {
      const canvas = document.createElement('canvas');
      const { width, height } = video;
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')?.drawImage(video, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/png');
      resolve(dataUrl);
    });
  });
};

export const deduplicateObjListByKey = (list: any[], key: string) => {
  return Array.from(new Set(list.map((item) => item[key]))).map((val) =>
    list.find((item) => item[key] === val),
  );
};

export const handleEchartsEvent = (myChart: EChartsType) => {
  let mouseOverIndex = 0;
  myChart.on('mouseover', (e) => {
    const overIndex = e.dataIndex;
    if (overIndex !== undefined) {
      mouseOverIndex = overIndex;
    }
    const seriesData = myChart.getOption().series as any;
    const labelData = seriesData[0].data as Record<string, string>[];
    const newLabelData = labelData.map((item, index) => {
      return { ...item, label: { show: index === mouseOverIndex } };
    });
    myChart.setOption({ series: { data: newLabelData } });
  });
};

export const getScrollbarWidth = () => {
  const isScroll = document.body.scrollHeight > window.innerHeight;
  if (!isScroll) {
    return 0;
  }
  const outerDiv = document.createElement('div');
  outerDiv.style.visibility = 'hidden';
  outerDiv.style.overflow = 'scroll';
  document.body.appendChild(outerDiv);

  const innerDiv = document.createElement('div');
  outerDiv.appendChild(innerDiv);

  const scrollbarWidth = outerDiv.offsetWidth - innerDiv.offsetWidth;
  document.body.removeChild(outerDiv);

  return scrollbarWidth;
};

export const isTimeStamp = (val: string) => {
  const regex = /^\d{10}$|^\d{13}$/;
  if (regex.test(String(val))) {
    const timeStamp = Number(val);
    const now = Date.now();
    if (
      (timeStamp >= 1000000000 && timeStamp <= Math.floor(now / 1000)) ||
      (timeStamp >= 1000000000000 && timeStamp <= now)
    ) {
      return true;
    }
  }
  return false;
};

export const isValidUUID = (uuid: string) => {
  const regex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return regex.test(uuid);
};

const indexByOrder = (orders: string[], key: string) => {
  const i = orders.findIndex((o) => key.includes(o));
  return i === -1 ? orders.length : i;
};

export const sortByOrder =
  (orders: string[]) =>
  (
    entryA: [string, Record<string, any>] | string,
    entryB: [string, Record<string, any>] | string,
  ) =>
    indexByOrder(orders, Array.isArray(entryA) ? entryA[0] : entryA) -
    indexByOrder(orders, Array.isArray(entryB) ? entryB[0] : entryB);
